import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@application/config';
import { map, Observable, timeout } from 'rxjs';
import {
  EmbeddedEnrollCancelRequest,
  EmbeddedEnrollRequest,
  EmbeddedEnrollResponse,
  EmbeddedTemplate,
  EmbeddedTemplateWire,
} from '../entities/profile-embedded-biometrics.model';

/**
 * Tempo máximo que deixamos o POST de cadastro pendurado.
 *
 * O contrato do backend fala em 2 minutos, e o aviso original era para clientes
 * `fetch`/`axios`, que cortam **antes** disso. Aqui o problema é o oposto: o
 * `HttpClient` sobre XHR não tem timeout nenhum, e sem esta rede o pedido ficaria
 * pendurado para sempre se a catraca sumisse a meio — deixando a UI presa em
 * "preparando o sensor" sem nunca resolver.
 */
const ENROLL_TIMEOUT_MS = 150_000;

@Injectable({ providedIn: 'root' })
export class ProfileEmbeddedBiometricsService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(APP_CONFIG).apiUrl;

  /**
   * Dispara o cadastro no sensor da catraca.
   *
   * Quem chama tem de ter assinado o grupo do equipamento **antes**: a placa
   * começa a preparar o sensor mal recebe o comando, e as primeiras notificações
   * de progresso perdem-se se a assinatura vier depois.
   */
  public enroll(
    edgeId: string,
    body: EmbeddedEnrollRequest
  ): Observable<EmbeddedEnrollResponse> {
    return this.http
      .post<EmbeddedEnrollResponse>(
        `${this.apiUrl.access_edge_manager_service}/v1/edge-command/${encodeURIComponent(
          edgeId
        )}/enroll-fingerprint`,
        body,
        { withCredentials: true }
      )
      .pipe(timeout(ENROLL_TIMEOUT_MS));
  }

  /**
   * Manda a catraca parar de esperar o dedo.
   *
   * Não substitui a resposta do `enroll`: o POST de cadastro continua pendurado
   * e é ele que fecha o ciclo, normalmente com `Cancelled` em 409. Se a placa já
   * tinha terminado, chega `Enrolled` na mesma — e é esse que vale.
   */
  public cancelEnroll(
    edgeId: string,
    body: EmbeddedEnrollCancelRequest
  ): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl.access_edge_manager_service}/v1/edge-command/${encodeURIComponent(
        edgeId
      )}/enroll-fingerprint/cancel`,
      body,
      { withCredentials: true }
    );
  }

  /**
   * Digitais embarcadas não revogadas da pessoa.
   *
   * O endpoint devolve `204 No Content` quando não há nenhuma, e nesse caso o
   * `HttpClient` entrega `null` — daí o `?? []`. Sem isso, um inquilino sem
   * nenhuma catraca LiteNet 2 fazia a lista rebentar em vez de aparecer vazia,
   * que é o caso normal e não erro.
   *
   * O `template` de cada item é descartado aqui, na fronteira.
   */
  public listTemplates(personId: string): Observable<EmbeddedTemplate[]> {
    return this.http
      .get<EmbeddedTemplateWire[] | null>(
        `${this.apiUrl.access_directory_service}/EmbeddedTemplate`,
        { params: { personId }, withCredentials: true }
      )
      .pipe(map((list) => (list ?? []).map(toEmbeddedTemplate)));
  }

  /**
   * Revoga uma digital embarcada.
   *
   * Exclusão lógica que vale para **todos** os equipamentos de uma vez. As
   * catracas apagam na sincronização seguinte, portanto a UI deve dizer
   * "revogada" e não "removida do equipamento".
   */
  public revokeTemplate(embeddedTemplateId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl.access_directory_service}/EmbeddedTemplate/Revoke`,
      { params: { embeddedTemplateId }, withCredentials: true }
    );
  }
}

function toEmbeddedTemplate(wire: EmbeddedTemplateWire): EmbeddedTemplate {
  return {
    EmbeddedTemplateId: wire.EmbeddedTemplateId,
    PersonId: wire.PersonId,
    SlotId: wire.SlotId ?? null,
    FingerPosition: wire.FingerPosition,
    IsDeleted: wire.IsDeleted ?? false,
  };
}
